// API contracts — the exact shape of everything that crosses the boundary
// between the web app and the API. Both sides import from here, so an endpoint
// can't change shape without breaking the other side's compile. That's the point.
//
// STACK.md §2.4: routes live under /api/v1, and old versions keep working when a
// new one ships (installed apps can't be forced to update).

import type {
  Album,
  AlbumWithCount,
  CalendarEvent,
  EventOccurrence,
  EventRepeat,
  GameId,
  GameResult,
  Illustration,
  Message,
  Milestone,
  Photo,
  Place,
  PlaceWithCount,
  Round,
  ScoreRow,
  TimedGameId,
  TimedRound,
  Word,
  WordWithGuesses,
  GameTheme,
} from "./types.js";
import type { UploadKind } from "./validation.js";

// ------------------------------------------------------------
//  Errors
// ------------------------------------------------------------

/**
 * The API's error codes. The code is for the client to decide what to do
 * (e.g. UNAUTHENTICATED → clear the token and go to /login); the message is for
 * a person to read.
 */
export type ErrorCode =
  | "VALIDATION" // 400 — the request doesn't make sense
  | "UNAUTHENTICATED" // 401 — no token, or invalid/expired
  | "FORBIDDEN" // 403 — authenticated but not allowed
  | "NOT_FOUND" // 404
  | "CONFLICT" // 409 — current state doesn't allow it (e.g. a round already running)
  | "TOO_LARGE" // 413 — file over the cap
  | "INTERNAL"; // 500 — our fault; never leaks details

/** The HTTP status for each code. One table, used by the API when responding. */
export const STATUS_BY_CODE: Record<ErrorCode, number> = {
  VALIDATION: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  TOO_LARGE: 413,
  INTERNAL: 500,
};

/**
 * The shape of EVERY error response, 500s included. A client that can read this
 * can read any error the API produces.
 *
 * `message` is the Portuguese sentence shown to the person — deliberate: it lets
 * the existing error JSX (`<p role="alert">{estado.erro}</p>`) stay as it is.
 */
export interface ErrorResponse {
  erro: {
    code: ErrorCode;
    message: string;
    /** The form field that's missing/invalid, when there's a useful one to point at. */
    field?: string;
  };
}

/** Type guard to tell an error body apart from a normal response. */
export function isErrorResponse(value: unknown): value is ErrorResponse {
  if (typeof value !== "object" || value === null) return false;
  const err = (value as ErrorResponse).erro;
  return (
    typeof err === "object" &&
    err !== null &&
    typeof err.code === "string" &&
    typeof err.message === "string"
  );
}

// ------------------------------------------------------------
//  Media — photos and illustrations with a ready-to-render signed URL
// ------------------------------------------------------------

/** A photo with its signed `src` (presigned GET, ~1 h). */
export interface PhotoWithSrc extends Photo {
  /** Temporary URL into the private bucket; null if signing failed. */
  src: string | null;
}

export interface IllustrationWithSrc extends Illustration {
  src: string | null;
}

export interface MilestoneWithPhotosSrc extends Milestone {
  fotos: PhotoWithSrc[];
}

export interface MessageWithSrc extends Message {
  /** Signed URL of the featured photo (null if there isn't one). */
  fotoSrc: string | null;
  /** Caption of the featured photo, if any. */
  fotoLegenda: string | null;
}

// ------------------------------------------------------------
//  Session
// ------------------------------------------------------------

/** Who is authenticated. Returned by GET /api/v1/me. */
export interface User {
  id: string;
  email: string;
  name: string;
}

// ------------------------------------------------------------
//  Milestones
// ------------------------------------------------------------

export interface CreateMilestone {
  titulo: string;
  data: string;
  categoria: string;
  descricao?: string | null;
  local?: string | null;
}

export type UpdateMilestone = CreateMilestone;

/** Trimmed list for the form's selects. */
export type MilestoneSummary = Pick<Milestone, "id" | "titulo" | "data">;

// ------------------------------------------------------------
//  Albums
// ------------------------------------------------------------

export interface CreateAlbum {
  nome: string;
  icone?: string;
}

export type UpdateAlbum = CreateAlbum;

export type { Album, AlbumWithCount };

/** Maximum album name length (the same on both sides). */
export const ALBUM_NAME_MAX = 60;

// ------------------------------------------------------------
//  Photos
// ------------------------------------------------------------

/**
 * The special "Conto" album: a reserved id that, in the album filter, shows the
 * illustrations instead of the photos — without duplicating files or rows.
 */
export const CONTO_ALBUM_ID = "conto";

export interface CreatePhoto {
  /** Comes from the upload flow (sign → PUT → confirm). */
  mediaId: string;
  legenda?: string | null;
  marcoId?: string | null;
  albumId?: string | null;
  /** Where it was taken, if we know (IDEA-014). */
  lugarId?: string | null;
}

export interface UpdatePhoto {
  legenda?: string | null;
  marcoId?: string | null;
  albumId?: string | null;
  lugarId?: string | null;
}

/** Move/delete several at once. `ids` is a real array (it used to be a CSV). */
export interface MovePhotos {
  ids: string[];
  albumId: string | null;
}

export interface DeletePhotos {
  ids: string[];
}

export type PhotoSummary = Pick<Photo, "id" | "legenda" | "criado_em">;

/**
 * A one-shot link that saves a photo to the device (IDEA-018).
 *
 * `filename` is what the browser will call the file — returned as well as being
 * baked into the signature, so the UI can say "A guardar Coração.jpg…".
 */
export interface PhotoDownload {
  url: string;
  filename: string;
}

// ------------------------------------------------------------
//  Illustrations (Conto)
// ------------------------------------------------------------

export interface CreateIllustration {
  mediaId: string;
  titulo: string;
  descricao?: string | null;
  data?: string | null;
  marcoId?: string | null;
}

export interface UpdateIllustration {
  titulo: string;
  descricao?: string | null;
  data?: string | null;
  marcoId?: string | null;
}

/** Reorder: one place up or down. */
export interface Reorder {
  direction: "up" | "down";
}

// ------------------------------------------------------------
//  Messages (Para sempre)
// ------------------------------------------------------------

export interface CreateMessage {
  titulo: string;
  corpo: string;
  ocasiaoData?: string | null;
  fotoDestaqueId?: string | null;
}

export type UpdateMessage = CreateMessage;

// ------------------------------------------------------------
//  Games
// ------------------------------------------------------------

/**
 * The whole scoreboard in one response: the overall total, each game's, and
 * each of the emoji game's themes.
 *
 * `overall` is the sum across **every** game — the emoji game's guesses plus
 * the timed games' finished rounds (IDEA-031/032). `byGame` is what the
 * per-game filter renders; `byTheme` stays the emoji game's own breakdown, so
 * nothing that already worked lost its detail.
 *
 * The /jogos page used to make 13 sequential calls to assemble this.
 */
export interface Scoreboard {
  overall: ScoreRow[];
  byGame: Record<GameId, ScoreRow[]>;
  /**
   * The emoji game's own breakdown. Counts **words guessed**, not points.
   *
   * It used to be points, back when each word scored 5/3/1 on its own. Since
   * v3 the points belong to the round, and a round spans three themes — there
   * is no honest way to split 5 points between them. Counting the words still
   * answers "who is better at Filmes?" and needs no arbitrary split.
   */
  byTheme: Record<GameTheme, ScoreRow[]>;
}

/**
 * A word as the *client* is allowed to see it. The answer (`palavra`) is only
 * filled in when this person may see it: if they composed it, or if it has been
 * guessed/revealed. Otherwise it comes back `null` — the API never sends it and
 * then trusts the client to hide it.
 */
export interface VisibleWord extends Omit<Word, "palavra"> {
  palavra: string | null;
  /** Did this person compose it? (decides who composes and who guesses) */
  mine: boolean;
  /**
   * The answer with un-hinted letters replaced by "_" — what the guesser sees
   * as dashes. Built on the server for the same reason `palavra` is withheld:
   * sending the word and masking it in the UI would put it in devtools.
   */
  mascara: string;
  /** Hints this word still allows — every letter but the last (IDEA-025 v3). */
  pistas_restantes: number;
}

export interface VisibleWordWithGuesses extends VisibleWord {
  palpites: WordWithGuesses["palpites"];
}

/** The round in progress, with words already filtered for whoever is asking. */
export interface ActiveRound {
  round: Round;
  words: VisibleWordWithGuesses[];
}

/** A finished round, for the history page. */
export interface FinishedRound {
  round: Round;
  words: VisibleWordWithGuesses[];
}

export interface SubmitWord {
  tema: string;
  palavra: string;
  emojis: string;
}

export interface SubmitGuess {
  texto: string;
  /**
   * The clock, in ms, sent with every guess so the *winning* one carries the
   * time. Counted in the browser like the two board games — the honour system
   * this app is built on (see services/timedGames.ts).
   */
  tempoMs?: number;
}

/** What the client needs to know after a guess. */
export interface GuessResult {
  correct: boolean;
  /** Wrong, but only just — for friendlier UI. Costs seconds, not a try. */
  nearMiss: boolean;
  /** Which try this was. Unlimited since v3. */
  attempt: number;
  /** The answer — only once it has actually been guessed. */
  palavra: string | null;
}

/** What comes back from spending a hint: one more letter, and the cost. */
export interface HintResult {
  mascara: string;
  pistas: number;
  pistas_restantes: number;
  /** Milliseconds this hint added to the round time. */
  penalizacao_ms: number;
}

// ------------------------------------------------------------
//  Timed games — Jogo da Memória and Shikaku (IDEA-031, IDEA-032)
// ------------------------------------------------------------

export type { GameId, TimedGameId, TimedRound, GameResult };

/**
 * A round of a timed game as the client needs it.
 *
 * `mine` is this person's result (null until they've played), `theirs` is the
 * other person's — but ONLY once this person has finished. Seeing the time to
 * beat before you start would change how you play, and there's no way to
 * un-know it, so the API withholds it rather than trusting the UI to hide it.
 * The same reasoning that masks the emoji game's answers.
 */
export interface TimedRoundView {
  round: TimedRound;
  mine: GameResult | null;
  theirs: GameResult | null;
  /**
   * Has the other person already played this board?
   *
   * One bit, and deliberately not the same thing as `theirs`. Their *time* is
   * withheld until you have played, because knowing "beat 1:42" changes how you
   * play and cannot be un-known. Knowing merely *that* they are done changes
   * nothing about how you play — but it decides what walking away means, since
   * quitting a round the other person has finished is a forfeit and quitting one
   * they have not is just leaving.
   */
  theyPlayed: boolean;
  /** Both of us have played — the round is over and the points are settled. */
  finished: boolean;
  /** Points this round awarded, once finished. */
  pontos: { email: string; points: number }[];
}

/** What the client posts when a board is cleared. */
export interface SubmitGameResult {
  /** Milliseconds on the clock. */
  tempoMs: number;
  /** Memória only — cards turned. */
  movimentos?: number | null;
  /** Shikaku and Sudoku — hints spent. Each adds HINT_PENALTY_MS to the time. */
  pistas?: number | null;
  /** Crossclimb only — whole rungs revealed. Charged apart, and dearer, than a hint. */
  revelacoes?: number | null;
  /** Sudoku only — digits that contradicted a peer. Counted, never charged. */
  erros?: number | null;
  /** Pinpoint only — guesses used. Lower is better; it IS the score. */
  palpites?: number | null;
  /** Walked away with the other person already finished — a forfeit. */
  desistiu?: boolean;
}

// ------------------------------------------------------------
//  Places — the map (IDEA-014)
// ------------------------------------------------------------

export type { Place, PlaceWithCount };

export interface CreatePlace {
  nome: string;
  latitude: number;
  longitude: number;
  descricao?: string | null;
  pais?: string | null;
  cidade?: string | null;
  data?: string | null;
  icone?: string;
}

export type UpdatePlace = CreatePlace;

/** Move photos onto (or off) a place — the album's multi-select, for the map. */
export interface MovePhotosToPlace {
  ids: string[];
  lugarId: string | null;
}

// ------------------------------------------------------------
//  Events — the shared calendar (IDEA-030)
// ------------------------------------------------------------

export type { CalendarEvent, EventOccurrence, EventRepeat };

export interface CreateEvent {
  titulo: string;
  data: string;
  descricao?: string | null;
  horaInicio?: string | null;
  horaFim?: string | null;
  diaInteiro?: boolean;
  local?: string | null;
  repete?: EventRepeat;
  icone?: string;
}

export type UpdateEvent = CreateEvent;

/**
 * What the calendar page renders: the events themselves, plus the occurrences
 * falling inside the month being viewed.
 *
 * Occurrences are expanded server-side so an annual event lands on the right
 * day even in a leap year, and the client never reimplements the arithmetic.
 */
export interface CalendarMonth {
  year: number;
  /** 1-based, as people say it. */
  month: number;
  occurrences: EventOccurrence[];
}

// ------------------------------------------------------------
//  Uploads (STACK.md §9)
// ------------------------------------------------------------

export interface SignUploadRequest {
  kind: UploadKind;
  filename: string;
  contentType: string;
  size: number;
}

export interface SignUploadResponse {
  mediaId: string;
  /** Presigned PUT, ~5 min, with the content type pinned. */
  uploadUrl: string;
}

export interface ConfirmUploadResponse {
  mediaId: string;
}

// ------------------------------------------------------------
//  Numbers — "O nosso amor em números" (IDEA-002)
// ------------------------------------------------------------

export interface OurNumbers {
  daysTogether: number;
  milestones: number;
  trips: number;
  photos: number;
  illustrations: number;
  messages: number;
  /** Places pinned on the map (IDEA-014). */
  places: number;
  /**
   * Distinct countries among those places.
   *
   * IDEA-002 deferred this because milestones only had a free-text `local`
   * with no structured country. The map gives us one, so the number is finally
   * real rather than guessed.
   */
  countries: number;
  /** Events in the shared calendar (IDEA-030). */
  events: number;
}

// ------------------------------------------------------------
//  Home screen (IDEA-029)
// ------------------------------------------------------------

/**
 * Everything the home screen's widgets need, in ONE response.
 *
 * The home screen is the first thing that loads, on a phone, often on mobile
 * data. Six widgets making six requests would mean six round-trips and six
 * separate loading shimmers arriving out of order. One endpoint assembles it
 * server-side, where the queries run in parallel against a local database.
 */
export interface HomeSummary {
  numbers: OurNumbers;
  /** The next thing in the calendar, expanded from its event. */
  nextEvent: EventOccurrence | null;
  /** The most recent photo, for the "última memória" widget. */
  latestPhoto: PhotoWithSrc | null;
  /** Days to the anniversary, and which one it will be. */
  anniversary: { daysRemaining: number; years: number };
  /** Rounds waiting for this person to play — every game, emoji included. */
  pendingGames: PendingGame[];
}

// ------------------------------------------------------------
//  Notifications (IDEA-044)
// ------------------------------------------------------------

/**
 * A round waiting for this person to play.
 *
 * `jogo` is a `GameId`, not a `TimedGameId`: the emoji game belongs in this
 * list too, and it was the one thing the home widget got wrong when it first
 * shipped — nine games nudged you, the tenth never did.
 *
 * Derived on every request from the round tables, never stored. See the note
 * over `notificacoes` in the schema for why the two are kept apart.
 */
export interface PendingGame {
  jogo: GameId;
  roundId: string;
}

export type NotificationType = "tua_vez" | "ronda_terminada";

/** One line of the notifications page. */
export interface Notification {
  id: string;
  tipo: NotificationType;
  jogo: GameId | null;
  ronda_id: string | null;
  /** Rendered when the notification was written — see the schema's note. */
  titulo: string;
  corpo: string;
  /** In-app path to open when it is tapped. */
  url: string;
  /** ISO timestamp, or null while unread. */
  lida_em: string | null;
  criado_em: string;
}

/**
 * The notifications page in one response.
 *
 * `naoLidas` travels with the list rather than being counted from it: the list
 * is capped, and a count computed from a capped list would stop growing at the
 * cap while the badge kept claiming otherwise.
 */
export interface NotificationList {
  notificacoes: Notification[];
  naoLidas: number;
}

/** What the browser needs before it can subscribe: the VAPID public key. */
export interface PushKey {
  /** Null when the server has no VAPID keys configured — push is simply off. */
  chavePublica: string | null;
}

/** A `PushSubscription`, flattened to what the server stores. */
export interface PushSubscriptionInput {
  endpoint: string;
  p256dh: string;
  auth: string;
}

// ------------------------------------------------------------
//  Health (STACK.md §2.8)
// ------------------------------------------------------------

export interface Health {
  ok: boolean;
  version: string;
  db: "ok" | "error";
}

// ------------------------------------------------------------
//  Form state
// ------------------------------------------------------------

/**
 * What a form returns after submitting. Keeps exactly the shape `useActionState`
 * used, so the forms' JSX doesn't have to change when Server Actions give way to
 * fetch calls.
 *
 * The field names stay Portuguese because the existing components destructure
 * them (`estado.erro`) in a dozen places.
 */
export interface FormState {
  erro: string | null;
  ok: boolean;
}

export const INITIAL_FORM_STATE: FormState = { erro: null, ok: false };
